import { ApolloError } from '@apollo/client/errors';
import { ErrorPage, LoadingPage, Modal, ModalForm, TControlledFormComponentProps, TControlledFormProps } from '@chocolate-soup-inc/cs-frontend-components';
import { joiResolver } from '@hookform/resolvers/joi';
import { useCallback, useMemo } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getProductFormFields } from '../../entities/products/formFields';
import { useFragmentOrFetchProduct } from '../../entities/products/queries';
import { getProductDefaultValues, getProductSchema } from '../../entities/products/schema';
import { TUpdateProductMutationInput, useUpdateProductMutation } from '../../generated/graphql';

export const GiftProductUpdate = () => {
  const { productId = '0' } = useParams();
  const navigate = useNavigate();
  const { data: product, loading, error } = useFragmentOrFetchProduct({ id: productId });

  const headline = useMemo(() => {
    return `Edit ${product?.description ? `"${product?.description}"` : 'Product'}`;
  }, [product]);

  const [updateProduct] = useUpdateProductMutation();

  const closeModal = useCallback(() => {
    navigate(generatePath('../../'));
  }, [navigate]);

  const onInvalidSubmit = useCallback<Exclude<TControlledFormProps<TUpdateProductMutationInput>['onInvalidSubmit'], undefined>>((errors: Record<string, any>) => {
    console.error(errors);
    const submitError = errors[''];
    if (submitError) toast.error(submitError.message);
  }, []);

  const onSubmit = useCallback<TControlledFormProps<TUpdateProductMutationInput>['onValidSubmit']>(
    (formData) => {
      return updateProduct({
        variables: {
          id: product?.id as string,
          version: product?._version as number,
          input: formData
        }
      })
        .then(() => {
          toast.success('Successfully updated the product.');
          closeModal();
        })
        .catch((error: ApolloError) => {
          toast.error('There was an error updating the product.');
          if (error instanceof ApolloError) error.graphQLErrors.forEach((err) => toast.error(err.message));
        });
    },
    [closeModal, updateProduct, product]
  );

  if (loading)
    return (
      <Modal closeModal={closeModal} headline={headline}>
        <LoadingPage />
      </Modal>
    );

  if (error) {
    console.error('query Apollo ERROR:', error);
    toast.error('An error has ocurred, check the console for details. (query Apollo ERROR)');
    //if (error.message) toast.error(error.message);
    if (!product)
      return (
        <Modal closeModal={closeModal} headline={headline}>
          <ErrorPage error={error} />
        </Modal>
      );
  }

  return (
    <ModalForm
      headline={headline}
      size='large'
      closeModal={closeModal}
      controlledFormProps={{
        fields: getProductFormFields() as TControlledFormComponentProps<TUpdateProductMutationInput>[],
        formProps: {
          defaultValues: getProductDefaultValues(product),
          resolver: joiResolver(getProductSchema(), {
            convert: true,
            abortEarly: false,
            stripUnknown: false
          })
        },
        onValidSubmit: onSubmit,
        onInvalidSubmit: onInvalidSubmit
      }}
    />
  );
};
